import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import React, { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { generateDefaultValues, schema, Schema } from "./schema";
import { ProjectsLocationsData } from "../../../../../../models/ProjectData";
import DataSvc from "../../../../../../services/dataSvc";
import { HookForm } from "../../../../../HookForm/HookForm";
import { HookFormTextarea } from "../../../../../HookForm/Textarea";

interface IProjectLocationCustomNotesFormProps {
  organizationId: string;
  projectLocation: ProjectsLocationsData;
  allowSubmit: boolean;
}

export const ProjectLocationCustomNotesForm = (
  props: IProjectLocationCustomNotesFormProps,
) => {
  const { organizationId, projectLocation, allowSubmit } = props;
  const { t: _t } = useTranslation();
  const t = (key: string) =>
    _t(
      `projectManagementPage.projectFacilityDetailsModal.projectLocationCustomNotes.${key}`,
    );

  const queryClient = useQueryClient();

  const defaultValues = useMemo(() => {
    return generateDefaultValues(projectLocation);
  }, [projectLocation]);

  const form = useForm<Schema>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const onSubmit = async (data: Schema) => {
    const payload = {
      custom_notes: data.customNotes,
    };
    try {
      await DataSvc.updateProjectLocation({
        organizationId: organizationId,
        projectId: projectLocation.project_id.toString(),
        projectLocationId: projectLocation.id.toString(),
        payload,
      });

      toast.success(t("success"));
      // Invalidate queries so data is refreshed
      // (This is necessary because if the user navigates away and back to this page, the memo field will be old)
      queryClient.invalidateQueries({
        queryKey: ["projectsLocations", projectLocation.project_id],
      });
    } catch (error) {
      // Do nothing, axios will handle the error
    }
  };

  return (
    <HookForm
      disableSubmit={!allowSubmit}
      showSubmitButton={allowSubmit}
      onSubmit={onSubmit}
      form={form}
      submitButtonLabel={t("update")}
    >
      <HookFormTextarea disabled={!allowSubmit} name="customNotes" />
    </HookForm>
  );
};
